<template>
  <PrmDropdown
    v-if="variant === 'single'"
    v-bind="shareProps"
    :show-clear="showClear"
    :model-value="modelValue"
    :virtual-scroller-options="virtualScrollerOptions"
    :option-disabled="optionDisabled"
    @update:model-value="onUpdate($event)"
    @filter="emit('filter', $event)"
    @hide="emit('hide')"
  >
    <template
      v-if="$slots.footer"
      #footer
    >
      <slot name="footer" />
    </template>
    <template
      #option="{
        option,
      }: {option: OPTION}"
    >
      <slot
        name="option"
        :option="option"
      >
        <QmulusDropdownOptionItem :option="option" />
      </slot>
    </template>
    <template
      #value="{
        placeholder,
      }: { placeholder: string | undefined }"
    >
      <template v-if="!isNullish(modelOption)">
        <slot
          name="value"
          :option="(modelOption as OPTION)"
          :value="(modelOption as OPTION).value"
        >
          <slot
            name="option"
            :option="(modelOption as OPTION)"
          >
            <QmulusDropdownOptionItem :option="(modelOption as OPTION)" />
          </slot>
        </slot>
      </template>
      <template v-else>
        <div class="p-dropdown-label p-placeholder qmulus-dropdown__placeholder">
          {{ placeholder ?? '\xa0' }}
        </div>
      </template>
    </template>
  </PrmDropdown>
  <PrmMultiselect
    v-else-if="variant === 'multi'"
    v-bind="shareProps"
    :model-value="modelValue"
    :virtual-scroller-options="virtualScrollerOptions"
    :option-disabled="optionDisabled"
    @update:model-value="onUpdate($event)"
    @filter="emit('filter', $event)"
    @hide="emit('hide')"
  >
    <template
      v-if="$slots.footer"
      #footer
    >
      <slot name="footer" />
    </template>
    <template
      #option="{
        option,
      }: {option: OPTION}"
    >
      <slot
        name="option"
        :option="option"
      >
        <QmulusDropdownOptionItem :option="option" />
      </slot>
    </template>
    <template
      #value="{
        placeholder,
      }: {placeholder: string | undefined}"
    >
      <template v-if="Array.isArray(modelOption) && modelOption.length > 0">
        <template
          v-for="(option, index) in limitedModelOption"
          :key="option.value"
        >
          <div :class="index < Math.min(modelOption.length, props.maxValuesDisplay) - 1 ? 'qmulus-dropdown__value-item qmulus-dropdown__value-item_with-separator' : 'qmulus-dropdown__value-item'">
            <slot
              name="value"
              :option="option"
              :value="option.value"
            >
              <slot
                name="option"
                :option="option"
              >
                <QmulusDropdownOptionItem :option="option" />
              </slot>
            </slot>
          </div>
        </template>
        <div
          v-if="(modelOption as OPTION[]).length > props.maxValuesDisplay"
          class="qmulus-dropdown__value-overflow"
        >
          +{{ (modelOption as OPTION[]).length - props.maxValuesDisplay }}
        </div>
      </template>
      <template v-else>
        {{ placeholder ?? '\xa0' }}
      </template>
    </template>
  </PrmMultiselect>
  <MultiselectWithConfirm
    v-else-if="variant === 'filter'"
    v-bind="shareProps"
    :options="props.options"
    :model-value="((modelValue ?? EMPTY_ARRAY) as OPTION[])"
    :virtual-scroller-options="virtualScrollerOptions"
    :option-disabled="optionDisabled"
    @update:model-value="onUpdate($event as TModelValue)"
    @filter="emit('filter', $event)"
    @hide="emit('hide')"
  >
    <template
      #option="{
        option,
      }: {option: OPTION}"
    >
      <slot
        name="option"
        :option="option"
      >
        <QmulusDropdownOptionItem :option="option" />
      </slot>
    </template>
    <template
      #value="{
        placeholder,
      }: {placeholder?: string | undefined}"
    >
      <template v-if="Array.isArray(modelOption) && modelOption.length > 0">
        <template
          v-for="(option, index) in limitedModelOption"
          :key="option.value"
        >
          <div :class="index < Math.min(modelOption.length, props.maxValuesDisplay) - 1 ? 'qmulus-dropdown__value-item qmulus-dropdown__value-item_with-separator' : 'qmulus-dropdown__value-item'">
            <slot
              name="value"
              :option="option"
              :value="option.value"
            >
              <slot
                name="option"
                :option="option"
              >
                <QmulusDropdownOptionItem :option="option" />
              </slot>
            </slot>
          </div>
        </template>
        <div
          v-if="(modelOption as OPTION[]).length > props.maxValuesDisplay"
          class="qmulus-dropdown__value-overflow"
        >
          +{{ (modelOption as OPTION[]).length - props.maxValuesDisplay }}
        </div>
      </template>
      <template v-else>
        {{ placeholder ?? '\xa0' }}
      </template>
    </template>
  </MultiselectWithConfirm>
</template>

<script setup lang='ts' generic="VALUE, OPTION extends TNamedOption<VALUE>, VARIANT extends TQmulusDropdownVariant">
import PrmDropdown, {
  DropdownProps,
} from '@NitrexGroup/primevue/dropdown';
import PrmMultiselect, {
  MultiSelectProps,
} from '@NitrexGroup/primevue/multiselect';
import {
  computed, useAttrs, watch,
} from 'vue';

import {
  EMPTY_ARRAY,
} from '../../constants/mutables.ts';
import {
  isNullish,
} from '../../utils/common.ts';
import MultiselectWithConfirm from '../multiselect-with-confirm/multiselect-with-confirm.vue';

import {
  TNamedOption, TQmulusDropdownVariant,
  isNamedOption,
} from './qmulus-dropdown';
import QmulusDropdownOptionItem from './qmulus-dropdown-option-item.vue';

defineOptions({
  inheritAttrs: false,
});

type TVirtualScrollerOptions = DropdownProps['virtualScrollerOptions'];

type TModelValue = VARIANT extends 'single' ? VALUE : VALUE[];
type TModelOption = VARIANT extends 'single' ? OPTION : OPTION[];
type TOptionKey = keyof OPTION;

const attrs = useAttrs();

const props = withDefaults(
  defineProps<{
    modelValue: TModelValue | null | undefined,
    options: OPTION[],
    variant: VARIANT,
    placeholder?: string,
    filter?: boolean,
    filterFields?: TOptionKey[] | TOptionKey,
    maxValuesDisplay?: number,
    showClear?: boolean,
    disabled?: boolean,
    loading?: boolean,
    invalid?: boolean,
    errorMessage?: string,
    virtualScrollerOptions?: TVirtualScrollerOptions,
    resetFilterOnHide?: boolean,
    optionDisabled?: (option: OPTION) => boolean,
  }>(),
  {
    maxValuesDisplay: 3,
    placeholder: undefined,
    filter: true,
    filterFields: undefined,
    errorMessage: undefined,
    virtualScrollerOptions: undefined,
    resetFilterOnHide: true,
    optionDisabled: undefined,
  },
);

const emit = defineEmits<{
  (e: 'update:modelValue', value: TModelValue | undefined): void,
  (e: 'update:modelOption', value: TModelOption | undefined): void,
  (e: 'filter', value: {
    value?: string,
  }): void,
  (e: 'hide'): void,
}>();

defineSlots<{
  footer?: any,
  option?: {
    option: OPTION,
  },
  value?: {
    value: VALUE,
    option: OPTION,
  },
}>();

const modelOption = computed<TModelOption | undefined>(() => {
  if (isNullish(props.modelValue)) {
    return undefined;
  }
  if (props.variant === 'single') {
    return props.options.find((option) => isNamedOption(option) ? option.value === props.modelValue : option === props.modelValue) as TModelOption | undefined;
  }
  return props.options.filter((option) => {
    if (Array.isArray(props.modelValue)) {
      return props.modelValue.includes(isNamedOption(option) ? option.value : option);
    }
    return false;
  }) as TModelOption;
});
watch(modelOption, () => emit('update:modelOption', modelOption.value));

const limitedModelOption = computed<OPTION[]>(() => {
  if (props.variant === 'single') {
    return [
      modelOption.value as OPTION,
    ];
  }
  return ((modelOption.value as OPTION[] | undefined) ?? EMPTY_ARRAY).slice(0, props.maxValuesDisplay);
});

const filterFieldArray = computed<string[] | undefined>(() => {
  if (isNullish(props.filterFields)) {
    return undefined;
  }
  if (Array.isArray(props.filterFields)) {
    return props.filterFields.map(String);
  }
  return [
    String(props.filterFields),
  ];
});

type TSharedPrimeVueProps = DropdownProps | MultiSelectProps;
const shareProps = computed<TSharedPrimeVueProps>(() => {
  return {
    ...attrs,
    disabled: props.disabled,
    loading: props.loading,
    invalid: props.invalid,
    placeholder: props.placeholder,
    filter: props.filter,
    autoFilterFocus: true,
    filterFields: filterFieldArray.value,
    resetFilterOnClear: true,
    resetFilterOnHide: props.resetFilterOnHide,
    options: props.options,
    optionValue: 'value',
    optionLabel: 'displayName',
  };
});

function onUpdate(value: TModelValue) {
  emit('update:modelValue', value);
}
</script>

<style scoped lang="scss">
.qmulus-dropdown{
  &__value-item {
    display: inline-flex;

    &_with-separator::after {
      content: ',\00a0'; /* \00a0 = NBSP */
    }
  }

  &__placeholder {
    width: 100%;
  }

  &__value-overflow {
    display: inline-block;
    margin-left: 0.5rem;
    color: var(--color-text-secondary);
  }
}
</style>
